/* eslint-disable max-lines */
import React from 'react';
import { data as historyCat } from '@lba-dev/package.local-globals/historyCat';
import { dayjs } from '@lba-dev/package.local-globals/dayjs';
import classnames from 'classnames';
import {
  HighlightOff,
  AddCircle,
  ArrowDropUp,
  ArrowDropDown
} from '@mui/icons-material';
import filesize from 'filesize';
import {
  DBANQ,
  DKBIS,
  DSTRAI,
  DURSSA,
  DRCPRO,
  DDECE,
  DCNI,
} from '@lba-dev/package.local-globals/docTypes';
import {
  calculFourniture,
  paymentDefault,
  getArtisanRemun,
  getMontant,
} from '@lba-dev/package.local-globals/comptaCalcules';
import { R_POR } from '@lba-dev/package.local-globals/remStatus';
import {
  MSQ_DET,
  MSQ_UT,
} from '@lba-dev/package.local-globals/questionTypeMSteps';
import { CHIEF, ADMIN } from '@lba-dev/package.local-globals/permissions';
import { List } from 'immutable';
import { services } from '@lba-dev/package.local-globals/getDataById';
import { permit } from '@lba-dev/package.local-globals/restrictions';
import {
  checkIfMobileNumber
} from '@lba-dev/package.local-globals/verifyNumber';
import {
  DOCUMENT_STATUS
} from '@lba-dev/package.local-globals/artisanDocumentCheck';
import { grey, green, red, orange } from '@mui/material/colors';

import previewInNewTab, { base64ToPdfElement } from './previewInNewTab';
import handlerFilter from '../components/List/data/handlerFilterColumns';
import store from '../store';

const priceFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

const style = {
  doc: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    margin: 10,
    padding: '2px 20px 2px 20px',
    borderRadius: 5,
    fontSize: 14,
    color: 'white',
  },
  textChipsDoc: {
    marginLeft: '15px',
  },
};

export const MAX_AUDIO_SIZE = filesize(16000000, { exponent: 2 });
export const MAX_FILES_SIZE = filesize(10000000, { exponent: 2 });
export const MAX_FILES = 20;

export const getSize = (files = []) =>
  filesize(
    files.reduce((a, v) => a + v.size, 0),
    { exponent: 2 }
  );

export const isMaxSizeOk = (files, max = MAX_FILES_SIZE) => {
  const ret = Number.parseFloat(getSize(files));
  max = Number.parseFloat(max, 10);
  return ret >= 0 && ret < max;
};

export const isMaxFileOk = (nb, max = MAX_FILES) => nb >= 0 && nb <= max;

export const deepCopy = obj => {
  let copy;
  if (obj instanceof Array) {
    copy = [...obj];
    copy.forEach((e, i) => {
      copy[i] = deepCopy(e);
    });
  } else if (typeof obj === 'object') {
    copy = { ...obj };
    Object.keys(obj).forEach(key => {
      obj[key] = deepCopy(obj[key]);
    });
  }
  return copy || obj;
};

export function cacheDisabled(type) {
  const obj = store.getState().config.find(t => t.type === type);
  return obj ? !obj.text : false;
}

export function getDataInObject(element, path, defaultValue = undefined) {
  if (!Array.isArray(path)) {
    if (typeof path !== 'string') {
      return defaultValue;
    }
    return typeof element[path] !== 'undefined' ? element[path] : defaultValue;
  }
  let i = 0;

  //eslint-disable-next-line
  while (path[i] && element && (element = element[path[i++]]));
  return typeof element !== 'undefined' ? element : defaultValue;
}

export function getAliasFromPhone(phone, def) {
  const user = store
    .getState()
    .users.find(
      (e) =>
        e.act &&
        (e.ligne === phone ||
          e.ligneService === phone ||
          e.multiLignes.includes(phone))
    );
  return user
    ? `${user.alias.charAt(0).toUpperCase() + user.alias.slice(1)
    } | ${services[user.service]?.name || ''}`
    : def || 'indisponible';
}

export function getUsersByService(service) {
  return store.getState().users.filter(u => u.act && u.service === service);
}

export function getGrandCompteName(_id, def) {
  const gc = store
    .getState()
    .grandCompte.find(e => e.get('_id').toLowerCase() === _id.toLowerCase());
  return gc ? gc.get('companyName') : def || 'automatique';
}

export function getRemunerationArtisan(inter) {
  return (
    inter.remunerationArtisan ||
    (inter.currentArtisan || {}).remuneration ||
    {}
  ).status === R_POR
    ? inter.finalPrice
    : inter.priceArtisan;
}

export function getGrandCompteMarge(inter) {
  const { lba, artisan } = calculFourniture(inter.supplies || []);
  const price = inter.finalPrice;
  const paiement = paymentDefault({
    rem: inter.currentArtisan ?
      getArtisanRemun(inter, inter.currentArtisan) : null,
    bp: getRemunerationArtisan(inter),
    f: { f: lba, r: artisan },
    finalPrice: inter.finalPrice,
    categorie: inter.categorie,
    loginAjoutIntervention: inter.login.ajout
  }
  );
  return (price
    ? (price - (lba + (getMontant(paiement, false) || 0))) / 100
    : 0
  ).toFixed(2);
}

export function getGrandCompteMargePercent(inter) {
  const price = getGrandCompteMarge(inter);
  const priceArtisan = inter.finalPrice;
  return priceArtisan ? (price / (priceArtisan / 10000)).toFixed(0) : 0;
}

export function getLogin(_id, def, users) {
  const user = (users || store.getState().users).find(e => e._id === _id);
  return user ? user.login : (def !== undefined && 'automatique') || '';
}

export function loginToId(login) {
  const user = store.getState().users.find(e => e.login === login);
  return user ? user._id : null;
}

export function getAlias(_id, def) {
  const user = store.getState().users.find(e => e._id === _id);
  return user ?
    `${user.alias.charAt(0).toUpperCase() + user.alias.slice(1)
    } | ${services[user.service]?.name || ''
    }` : def || '';
}

export function getCustomFromUser(_id, field) {
  const user = store.getState().users.find(e => e._id === _id);
  return user ? user[field] : '';
}

export function getUser(_id) {
  return store.getState().users.find(e => e._id === _id);
}

export function getType(i) {
  const type = historyCat.find(e => e._id === i);
  return type ? type.name : '';
}

export function getdayjsOrDate(date) {
  return `${dayjs(date).format('L')} à
    ${dayjs(date).format('HH[h]mm')}`;
}

export function divide(first, second, fixed = 0) {
  return Number(first / second || 0).toFixed(fixed);
}

export function formatPrice(number) {
  return priceFormatter.format(number || 0);
}


/**
 * Calcule le statut normalisé d'un document spécifique pour un artisan.
 * Cette fonction ne s'occupe que de la logique (le TEXTE), pas de la couleur.
 */
export const getArtisanDocumentStatus = (docType, artisan) => {
  // 1. Récupérer le statut injecté par le serveur s'il existe
  let documentStatus;
  switch (docType) {
    case DURSSA:
      documentStatus = artisan.documentStatus?.urssaf;
      break;
    case DRCPRO:
      documentStatus = artisan.documentStatus?.assuranceRCPro;
      break;
    case DDECE:
      documentStatus = artisan.documentStatus?.assuranceDecennale;
      break;
    case DKBIS:
      documentStatus = artisan.documentStatus?.kbis;
      break;
    case DCNI:
      documentStatus = artisan.documentStatus?.cni;
      break;
    case DBANQ: {
      const hasSpecialBankCondition = artisan.verifRIB === false &&
        (artisan.newIban?.length || artisan.newBic?.length);

      if (hasSpecialBankCondition) {
        documentStatus = DOCUMENT_STATUS.PENDING;
      } else {
        documentStatus = artisan.documentStatus?.rib;
      }
      break;
    }
    case DSTRAI:
      documentStatus = artisan.documentStatus?.contrat;
      break;
    default:
      documentStatus = null;
  }

  return documentStatus;
};

/**
 * Retourne le style (couleur/border) d'un document pour l'affichage.
 */
export const getDocumentColor = (status) => {
  switch (status) {
    case DOCUMENT_STATUS.VALID:
      return { backgroundColor: green[500] };
    case DOCUMENT_STATUS.EXPIRED:
      return { backgroundColor: grey[500] };
    case DOCUMENT_STATUS.PENDING:
      return { backgroundColor: orange[500] };
    case DOCUMENT_STATUS.SPECIAL:
      return {
        backgroundColor: 'transparent',
        border: '1px solid black',
        color: 'black',
        specialCase: true,
      };
    case DOCUMENT_STATUS.NONE:
    default:
      return { backgroundColor: red[500] };
  }
};

/**
 * Retourne le style (couleur/border) d'un document pour l'affichage.
 */
export const checkDocument = (docType, artisan) => {
  const status = getArtisanDocumentStatus(docType, artisan);
  return getDocumentColor(status);
};

const document = [
  { type: DSTRAI, title: 'Contrat' },
  { type: DKBIS, title: 'KBIS' },
  { type: DCNI, title: 'CNI' },
  { type: DBANQ, title: 'RIB' },
  { type: DURSSA, title: 'URSSAF' },
  { type: DRCPRO, title: 'Assurance RC Pro' },
  { type: DDECE, title: 'Assurance Décennale' },
];

export const GetDoc = ({ artisan, handleGetDoc }) => (
  <div style={{ marginTop: 5 }}>
    {document.map((doc) => {
      const styleResult = checkDocument(doc.type, artisan);
      const specialCase = styleResult.specialCase;
      const icon = specialCase ? (
        <HighlightOff />
      ) : (
        handleGetDoc && <AddCircle />
      );
      return (
        <div
          key={doc.title}
          style={{ ...style.doc, ...styleResult }}
          data-title={doc.title}
          onClick={e => (handleGetDoc ? handleGetDoc(e) : e)}
        >
          <span>{icon}</span>
          <span style={{
            ...style.textChipsDoc,
            color: specialCase ? 'black' : undefined
          }}>
            {doc.title}
          </span>
        </div>
      );
    })}
  </div>
);

export const showFilter = (elem, classes, handlerFilterByColumns, name) => {
  if (handlerFilter[name] && handlerFilter[name][elem]) {
    return (
      <span
        value={elem}
        className={classes.posBtn}
        onClick={handlerFilterByColumns}
      >
        <ArrowDropUp
          className={classnames(classes.btnFilter, classes.arrowUp)}
        />
        <ArrowDropDown
          className={classnames(classes.btnFilter, classes.arrowDown)}
        />
      </span>
    );
  }
  return undefined;
};

export const openPDF = async (data, title) =>
  previewInNewTab(await base64ToPdfElement(data), title);

export const formatNumberPhone = number =>
  number.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');

export const checkPhoneNumber = (value) => checkIfMobileNumber(value);

export const throwIt = e => {
  throw e;
};

export const checkExpiry = e => e.cardInfo?.expiry && dayjs().isBefore({
  month: e.cardInfo.expiry.substring(e.cardInfo.expiry.search('^0') + 1, 2) - 1,
  year: e.cardInfo.expiry.substring(3)
});

export const getEquipes = ({ user, users = [] }) => {
  const usersArr = List.isList(users) ? users.toJS() : users;
  const userTeam = user.equipe && user.equipe.ids.length
    ? [user, ...usersArr.filter(u => user.equipe.ids.includes(u._id))]
    : [user];
  if (permit(user, { key: 'commercialAdminAcces' })) {
    return usersArr;
  }
  else if (user.permission > ADMIN && user.permission <= CHIEF) {
    return userTeam;
  }
  return [user];
};

export const firstCallDiff = (firstCall, ajout) => {
  const diff = dayjs(firstCall).diff(dayjs(ajout), 'minutes');
  if (diff < 15) {
    return '-15 min';
  } else if (diff >= 15 && diff < 30) {
    return '30 min';
  } else if (diff >= 30 && diff < 60) {
    return 'Dans l\'heure';
  }
  return 'Hors delais';
};

export const firstCallFilters = (value) => {
  const ranges = {
    '-15 min': { max: 15 },
    '30 min': { min: 15, max: 30 },
    'Dans l\'heure': { min: 30, max: 60 },
    'Hors delais': { min: 60 }
  };
  const range = ranges[
    Object.keys(ranges).find(e => new RegExp(value, 'i').test(e))
  ];
  return {
    'date.firstCall': { $exists: true },
    ...(range && !range.min
      ? {
        $expr: {
          $lt: [
            { '$subtract': ['$date.firstCall', '$date.ajout'] },
            1000 * 60 * range.max
          ]
        }
      }
      : {}
    ),
    ...(range && !range.max
      ? {
        $expr: {
          $gt: [
            { '$subtract': ['$date.firstCall', '$date.ajout'] },
            1000 * 60 * range.min
          ]
        }
      }
      : {}
    ),
    ...(range && range.max && range.min
      ? {
        $expr: {
          $and: [
            {
              $lt: [
                { '$subtract': ['$date.firstCall', '$date.ajout'] },
                1000 * 60 * range.max
              ],
            },
            {
              $gt: [
                { '$subtract': ['$date.firstCall', '$date.ajout'] },
                1000 * 60 * range.min
              ]
            }
          ]
        }
      }
      : {}
    ),
  };
};

/**
 * @param {string} str
 */
export function getCapitalLetters(str) {
  return str.match(/[A-Z]/g).join('');
}

export const artisanKeys = (user) => permit(
  user, { key: 'displayArtisanCompanyName' })
  ? ['currentArtisan.companyName', '']
  : ['currentArtisan.name', 'currentArtisan.firstname'];

/**
 * @param {QuestionMetier[]} questions
 */
export const computeCountImportant = (questions) =>
  (questions || []).reduce((acc, curr) => {
    acc +=
      [MSQ_DET, MSQ_UT].includes(curr.questionType) &&
        (curr.responses || []).length &&
        curr.responses.some((s) => s.important) ? 1 : 0;
    return acc;
  }, 0);

/**
 * @param {QuestionMetier[]} questions
 * @param {(question: QuestionMetier, index, result: any) => void} cb
 * @param {any} result
*/
export const mapDeepQuestionWithBehavior = (questions, cb, result = {}) => {
  questions.forEach((question, index) => {
    cb(question, index, result);
    if (question.responses) {
      question.responses?.forEach((response) => {
        if (response.subQuestions) {
          mapDeepQuestionWithBehavior(
            response.subQuestions,
            cb,
            result
          );
        }
      });
    }
  });

  return result;
};

/**
 * @param {string} str
 */
export function cleanString(str) {
  return str.replace(/\s+/g, ' ').trim();
}

/**
 * @param {string} str
 */
export function removeAccents(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Re-export metier utilities from central location
export {
  getMetierName,
  getMetierCollection,
  getPaymentModes,
  isUsingFallbackDesc,
  truncateForDisplay,
} from './metierUtils';
